/**
 * resolveBack — the single source of back-navigation truth.
 *
 * Pure function. No React, no Zustand, no React Router imports.
 * Given the current stack snapshot and the back source, returns the
 * action the dispatcher should perform.
 *
 * Priority order (per docs/12_NAVIGATION_ACTIVITY_MODEL.md, decision D4):
 *   1. Blocking dialog open                  → close-dialog
 *   2. Source is "close-button"              → close-activity (top)
 *   3. Top activity has multiple entries     → pop-entry
 *   4. Top activity exists (single entry)    → close-activity
 *   5. No activity, mainPathname !== "/"     → main-tab-back
 *   6. No activity, mainPathname === "/"     → exit-app
 *
 * Notes:
 *   - close-button is the "close everything in this activity" escape hatch.
 *     It skips the entry-pop step even when entries.length > 1.
 *   - hardware / browser / in-app sources all step back one level at a time.
 *   - "noop" is reserved for future use (e.g. when a transition is in flight);
 *     this implementation never returns it.
 */

import type { BackAction, BackInput } from "./types";

export function resolveBack(input: BackInput): BackAction {
  const { stack, source, mainPathname } = input;

  const topDialog = stack.dialogs[stack.dialogs.length - 1];
  if (topDialog) {
    return { type: "close-dialog", dialogId: topDialog.id };
  }

  const top = stack.activities[stack.activities.length - 1];

  if (top) {
    if (source === "close-button") {
      return { type: "close-activity", activityId: top.id };
    }
    if (top.entries.length > 1) {
      const topEntry = top.entries[top.entries.length - 1];
      return {
        type: "pop-entry",
        activityId: top.id,
        entryId: topEntry.id,
      };
    }
    return { type: "close-activity", activityId: top.id };
  }

  if (mainPathname === "/") {
    return { type: "exit-app" };
  }
  return { type: "main-tab-back", from: mainPathname };
}
